import {
  summarizePemaxPayload,
  summarizePimaxPayload,
  summarizeSIndexPayload,
  type PemaxResultPayload,
  type PimaxResultPayload,
  type SIndexResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import { normalizePeakCoughFlowPayload, summarizePeakCoughFlowPayload } from '../peak-cough-flow/peakCoughFlowDomain';

export type ClinicalReferencePurpose =
  | 'healthy_reference'
  | 'screening'
  | 'diagnostic_support'
  | 'disease_status'
  | 'prognostic'
  | 'MCID'
  | 'MDC'
  | 'action_zone'
  | 'descriptive';

export type ClinicalEvidenceStatus =
  | 'validated'
  | 'proposed'
  | 'derived'
  | 'institutional'
  | 'unavailable';

export type ClinicalRuleLifecycle =
  | 'draft'
  | 'verified'
  | 'clinically_approved'
  | 'licensed'
  | 'active'
  | 'retired';

export type ClinicalReferenceExclusionReason =
  | 'legacy_instrument_version'
  | 'unlicensed_version'
  | 'wrong_protocol'
  | 'wrong_diagnosis'
  | 'out_of_age_range'
  | 'missing_input'
  | 'classification_unavailable';

export interface ClinicalCitation {
  id: string;
  shortLabel: string;
  citation: string;
  url: string;
}

export interface ClinicalReferenceRuleDefinition {
  id: string;
  version: number;
  instrumentCode: string;
  instrumentVersion: string;
  purpose: ClinicalReferencePurpose;
  evidenceStatus: ClinicalEvidenceStatus;
  lifecycle: ClinicalRuleLifecycle;
  reportLabel: string;
  citationIds: string[];
  classificationAvailable: boolean;
}

export interface ClinicalReferenceRuleSnapshot {
  id: string;
  version: number;
  purpose: ClinicalReferencePurpose;
  evidenceStatus: ClinicalEvidenceStatus;
  reportLabel: string;
  citationIds: string[];
}

export interface ClinicalReferenceExcludedRuleSnapshot {
  ruleId: string;
  ruleVersion: number;
  reason: ClinicalReferenceExclusionReason;
  explanation: string;
}

export interface ClinicalReferenceDecisionSnapshot {
  engineVersion: string;
  evaluatedAt: string;
  instrumentCode: string;
  instrumentVersion: string;
  descriptiveOnly: boolean;
  selectedRules: ClinicalReferenceRuleSnapshot[];
  excludedRules: ClinicalReferenceExcludedRuleSnapshot[];
  citations: ClinicalCitation[];
}

export const CLINICAL_REFERENCE_ENGINE_VERSION = '2026.11.01.1';

export const ASSESSMENT_CURRENT_TEST_VERSIONS: Readonly<Record<string, number>> = {
  INITIAL_ASSESSMENT: 1,
  HADS: 2,
  LCADL: 2,
  PEAK_COUGH_FLOW: 2,
};

export const getCurrentAssessmentTestVersion = (testKey: string): number =>
  ASSESSMENT_CURRENT_TEST_VERSIONS[testKey] ?? 1;

export const CLINICAL_CITATIONS: Readonly<Record<string, ClinicalCitation>> = {
  SINGH_1992_ISWT: {
    id: 'SINGH_1992_ISWT',
    shortLabel: 'Singh et al. (1992)',
    citation: 'Singh SJ et al. Development of a shuttle walking test of disability in patients with chronic airways obstruction. Thorax. 1992;47:1019-1024.',
    url: 'https://doi.org/10.1136/thx.47.12.1019',
  },
  PROBST_2012_ISWT: {
    id: 'PROBST_2012_ISWT',
    shortLabel: 'Probst et al. (2012)',
    citation: 'Probst VS et al. Reference values for the incremental shuttle walking test. Respir Med. 2012;106:243-248.',
    url: 'https://doi.org/10.1016/j.rmed.2011.07.023',
  },
  REVILL_1999_ESWT: {
    id: 'REVILL_1999_ESWT',
    shortLabel: 'Revill et al. (1999)',
    citation: 'Revill SM et al. The endurance shuttle walk: a new field test for assessment of endurance capacity in COPD. Thorax. 1999;54:213-222.',
    url: 'https://doi.org/10.1136/thx.54.3.213',
  },
  HILL_2012_ESWT: {
    id: 'HILL_2012_ESWT',
    shortLabel: 'Hill et al. (2012)',
    citation: 'Hill K et al. A simple method to derive speed for the endurance shuttle walk test. Respir Med. 2012;106:1665-1670.',
    url: 'https://doi.org/10.1016/j.rmed.2012.08.011',
  },
  ALBUQUERQUE_2022_6MST: {
    id: 'ALBUQUERQUE_2022_6MST',
    shortLabel: 'Albuquerque et al. (2022)',
    citation: 'Albuquerque VS et al. Normative values and reference equation for the six-minute step test. J Bras Pneumol. 2022;48:e20210511.',
    url: 'https://doi.org/10.36416/1806-3756/e20210511',
  },
  SYKES_2004_CHESTER: {
    id: 'SYKES_2004_CHESTER',
    shortLabel: 'Sykes e Roberts (2004)',
    citation: 'Sykes K, Roberts A. The Chester step test—a simple yet effective tool for prediction of aerobic capacity. Physiotherapy. 2004;90:183-188.',
    url: 'https://doi.org/10.1016/j.physio.2004.03.008',
  },
  KARLOH_2013_CHESTER: {
    id: 'KARLOH_2013_CHESTER',
    shortLabel: 'Karloh et al. (2013)',
    citation: 'Karloh M et al. Chester step test in patients with COPD and healthy subjects. Braz J Phys Ther. 2013;17:227-235.',
    url: 'https://doi.org/10.1590/S1413-35552012005000087',
  },
  TOMKINSON_2025_HANDGRIP: {
    id: 'TOMKINSON_2025_HANDGRIP',
    shortLabel: 'Tomkinson et al. (2025)',
    citation: 'Tomkinson GR et al. International norms for adult handgrip strength. J Sport Health Sci. 2025;14:101014.',
    url: 'https://doi.org/10.1016/j.jshs.2024.101014',
  },
  GARCIA_2024_PERIPHERAL: {
    id: 'GARCIA_2024_PERIPHERAL',
    shortLabel: 'Garcia et al. (2024)',
    citation: 'Garcia T et al. Characteristics of Skeletal Muscle Strength in Subjects With Interstitial Lung Disease. Arch Phys Med Rehabil. 2024;105:1099-1105.',
    url: 'https://doi.org/10.1016/j.apmr.2024.01.006',
  },
  MAYHEW_1992_1RM: {
    id: 'MAYHEW_1992_1RM',
    shortLabel: 'Mayhew et al. (1992)',
    citation: 'Mayhew JL et al. Relative muscular endurance performance as a predictor of bench press strength. J Appl Sport Sci Res. 1992;6:200-206.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/7500624/',
  },
  WATHAN_1994_1RM: {
    id: 'WATHAN_1994_1RM',
    shortLabel: 'Wathan (1994)',
    citation: 'Wathan D. Load assignment. In: Baechle TR, ed. Essentials of Strength Training and Conditioning. Human Kinetics; 1994:435-446.',
    url: 'https://us.humankinetics.com/products/essentials-of-strength-training-and-conditioning-4th-edition-with-web-resource',
  },
  NEDER_1999_RESP_PRESSURES: {
    id: 'NEDER_1999_RESP_PRESSURES',
    shortLabel: 'Neder et al. (1999)',
    citation: 'Neder JA et al. Reference values for lung function tests II: maximal respiratory pressures and voluntary ventilation. Braz J Med Biol Res. 1999;32:719-727.',
    url: 'https://doi.org/10.1590/S0100-879X1999000600007',
  },
  REIS_2025_SINDEX: {
    id: 'REIS_2025_SINDEX',
    shortLabel: 'Reis et al. (2025)',
    citation: 'Reis LFF et al. Reference equation for maximal dynamic inspiratory muscle pressure index in healthy Brazilian adults. J Bras Pneumol. 2025;e20240312.',
    url: 'https://doi.org/10.36416/1806-3756/e20240312',
  },
  SOUZA_2025_SINDEX: {
    id: 'SOUZA_2025_SINDEX',
    shortLabel: 'Souza et al. (2025)',
    citation: 'Souza VC et al. Maximal dynamic inspiratory pressure: S-Index prediction values and diagnosis accuracy. J Bras Pneumol. 2025;51:e20240409.',
    url: 'https://doi.org/10.36416/1806-3756/e20240409',
  },
  LAVENEZIANA_2019_ENDURANCE: {
    id: 'LAVENEZIANA_2019_ENDURANCE',
    shortLabel: 'Laveneziana et al. (2019)',
    citation: 'Laveneziana P et al. ERS statement on respiratory muscle testing at rest and during exercise. Eur Respir J. 2019;53:1801214.',
    url: 'https://doi.org/10.1183/13993003.01214-2018',
  },
  KHAN_2023_NEUROMUSCULAR: {
    id: 'KHAN_2023_NEUROMUSCULAR',
    shortLabel: 'Khan et al. (2023)',
    citation: 'Khan A et al. Respiratory Management of Patients With Neuromuscular Weakness. Chest. 2023;164:394-413.',
    url: 'https://doi.org/10.1016/j.chest.2023.03.011',
  },
  CHILDS_2024_DMD: {
    id: 'CHILDS_2024_DMD',
    shortLabel: 'Childs et al. (2024)',
    citation: 'Childs AM et al. Development of respiratory care guidelines for Duchenne muscular dystrophy in the UK. Thorax. 2024;79:476-485.',
    url: 'https://doi.org/10.1136/thorax-2023-220811',
  },
  BACH_1996_COUGH: {
    id: 'BACH_1996_COUGH',
    shortLabel: 'Bach e Saporito (1996)',
    citation: 'Bach JR, Saporito LR. Criteria for extubation and tracheostomy tube removal for patients with ventilatory failure. Chest. 1996;110:1566-1571.',
    url: 'https://doi.org/10.1378/chest.110.6.1566',
  },
  BIANCHI_2008_COUGH: {
    id: 'BIANCHI_2008_COUGH',
    shortLabel: 'Bianchi e Baiardi (2008)',
    citation: 'Bianchi C, Baiardi P. Cough peak flows: standard values for children and adolescents. Am J Phys Med Rehabil. 2008;87:461-467.',
    url: 'https://doi.org/10.1097/PHM.0b013e318174e4c7',
  },
  CARDOSO_2012_COUGH: {
    id: 'CARDOSO_2012_COUGH',
    shortLabel: 'Cardoso et al. (2012)',
    citation: 'Cardoso FEF et al. Evaluation of peak cough flow in Brazilian healthy adults. Int Arch Med. 2012;5:25.',
    url: 'https://doi.org/10.1186/1755-7682-5-25',
  },
  BRENNAN_2022_COUGH: {
    id: 'BRENNAN_2022_COUGH',
    shortLabel: 'Brennan et al. (2022)',
    citation: 'Brennan M et al. The use of cough peak flow in the assessment of respiratory function in clinical practice - A narrative literature review. Respir Med. 2022;193:106740.',
    url: 'https://doi.org/10.1016/j.rmed.2022.106740',
  },
  FROMAGEOT_2001_POSTURAL_VC: {
    id: 'FROMAGEOT_2001_POSTURAL_VC',
    shortLabel: 'Fromageot et al. (2001)',
    citation: 'Fromageot C et al. Supine fall in lung volumes in the assessment of diaphragmatic weakness in neuromuscular disorders. Arch Phys Med Rehabil. 2001;82:123-128.',
    url: 'https://doi.org/10.1053/apmr.2001.18053',
  },
  JI_2021_PEF: {
    id: 'JI_2021_PEF',
    shortLabel: 'Ji et al. (2021)',
    citation: 'Ji C et al. Reference values and related factors for peak expiratory flow in middle-aged and elderly Chinese. Front Public Health. 2021;9:706524.',
    url: 'https://doi.org/10.3389/fpubh.2021.706524',
  },
  NATHAN_2004_ACT: {
    id: 'NATHAN_2004_ACT',
    shortLabel: 'Nathan et al. (2004)',
    citation: 'Nathan RA et al. Development of the Asthma Control Test. J Allergy Clin Immunol. 2004;113:59-65.',
    url: 'https://doi.org/10.1016/j.jaci.2003.09.008',
  },
  ROXO_2010_ACT: {
    id: 'ROXO_2010_ACT',
    shortLabel: 'Roxo et al. (2010)',
    citation: 'Roxo JPF et al. Portuguese-language version of the Asthma Control Test: validation for use in Brazil. J Bras Pneumol. 2010;36:159-166.',
    url: 'https://doi.org/10.1590/S1806-37132010000200002',
  },
  BESTALL_1999_MMRC: {
    id: 'BESTALL_1999_MMRC',
    shortLabel: 'Bestall et al. (1999)',
    citation: 'Bestall JC et al. Usefulness of the MRC dyspnoea scale as a measure of disability in COPD. Thorax. 1999;54:581-586.',
    url: 'https://doi.org/10.1136/thx.54.7.581',
  },
  KOVELIS_2008_MMRC_PFSDQ: {
    id: 'KOVELIS_2008_MMRC_PFSDQ',
    shortLabel: 'Kovelis et al. (2008)',
    citation: 'Kovelis D et al. Validation of PFSDQ-M and the MRC scale for use in Brazilian patients with COPD. J Bras Pneumol. 2008;34:1008-1018.',
    url: 'https://doi.org/10.1590/S1806-37132008001200005',
  },
  LAREAU_1998_PFSDQ: {
    id: 'LAREAU_1998_PFSDQ',
    shortLabel: 'Lareau et al. (1998)',
    citation: 'Lareau SC et al. Development and testing of the modified PFSDQ. Heart Lung. 1998;27:159-168.',
    url: 'https://doi.org/10.1016/S0147-9563(98)90003-6',
  },
  ZIGMOND_1983_HADS: {
    id: 'ZIGMOND_1983_HADS',
    shortLabel: 'Zigmond e Snaith (1983)',
    citation: 'Zigmond AS, Snaith RP. The Hospital Anxiety and Depression Scale. Acta Psychiatr Scand. 1983;67:361-370.',
    url: 'https://doi.org/10.1111/j.1600-0447.1983.tb09716.x',
  },
  BOTEGA_1995_HADS: {
    id: 'BOTEGA_1995_HADS',
    shortLabel: 'Botega et al. (1995)',
    citation: 'Botega NJ et al. Mood disorders among medical in-patients: validation of HADS in Brazil. Rev Saude Publica. 1995;29:355-363.',
    url: 'https://doi.org/10.1590/S0034-89101995000500004',
  },
  BUYSSE_1989_PSQI: {
    id: 'BUYSSE_1989_PSQI',
    shortLabel: 'Buysse et al. (1989)',
    citation: 'Buysse DJ et al. The Pittsburgh Sleep Quality Index. Psychiatry Res. 1989;28:193-213.',
    url: 'https://doi.org/10.1016/0165-1781(89)90047-4',
  },
  BERTOLAZI_2011_PSQI: {
    id: 'BERTOLAZI_2011_PSQI',
    shortLabel: 'Bertolazi et al. (2011)',
    citation: 'Bertolazi AN et al. Validation of the Brazilian Portuguese PSQI. Sleep Med. 2011;12:70-75.',
    url: 'https://doi.org/10.1016/j.sleep.2010.04.020',
  },
  JUNIPER_1992_AQLQ: {
    id: 'JUNIPER_1992_AQLQ',
    shortLabel: 'Juniper et al. (1992)',
    citation: 'Juniper EF et al. Development of the Asthma Quality of Life Questionnaire. Thorax. 1992;47:76-83.',
    url: 'https://doi.org/10.1136/thx.47.2.76',
  },
  JONES_1992_SGRQ: {
    id: 'JONES_1992_SGRQ',
    shortLabel: 'Jones et al. (1992)',
    citation: 'Jones PW et al. The St George\'s Respiratory Questionnaire. Am Rev Respir Dis. 1992;145:1321-1327.',
    url: 'https://doi.org/10.1164/ajrccm/145.6.1321',
  },
  GARROD_2000_LCADL: {
    id: 'GARROD_2000_LCADL',
    shortLabel: 'Garrod et al. (2000)',
    citation: 'Garrod R et al. Development and validation of the London Chest Activity of Daily Living scale. Respir Med. 2000;94:589-596.',
    url: 'https://doi.org/10.1053/rmed.2000.0786',
  },
  CARPES_2008_LCADL: {
    id: 'CARPES_2008_LCADL',
    shortLabel: 'Carpes et al. (2008)',
    citation: 'Carpes MF et al. Brazilian Portuguese version of the LCADL scale. J Bras Pneumol. 2008;34:143-151.',
    url: 'https://jbp.org.br/export-pdf/698/2008_34_3_4_english.pdf',
  },
  RECTOR_1993_MLHFQ: {
    id: 'RECTOR_1993_MLHFQ',
    shortLabel: 'Rector et al. (1993)',
    citation: 'Rector TS et al. Validity of the Minnesota Living with Heart Failure Questionnaire. Am J Cardiol. 1993;71:1106-1107.',
    url: 'https://doi.org/10.1016/0002-9149(93)90582-W',
  },
  BEHLOULI_2009_MLHFQ: {
    id: 'BEHLOULI_2009_MLHFQ',
    shortLabel: 'Behlouli et al. (2009)',
    citation: 'Behlouli H et al. Identifying relative cut-off scores for interpretation of the MLHFQ. IEEE EMBS. 2009:6242-6246.',
    url: 'https://doi.org/10.1109/IEMBS.2009.5334659',
  },
  SPERTUS_1995_SAQ: {
    id: 'SPERTUS_1995_SAQ',
    shortLabel: 'Spertus et al. (1995)',
    citation: 'Spertus JA et al. Development and evaluation of the Seattle Angina Questionnaire. J Am Coll Cardiol. 1995;25:333-341.',
    url: 'https://doi.org/10.1016/0735-1097(94)00397-9',
  },
  CHAN_2014_SAQ7: {
    id: 'CHAN_2014_SAQ7',
    shortLabel: 'Chan et al. (2014)',
    citation: 'Chan PS et al. Development and validation of a short version of the Seattle Angina Questionnaire. Circ Cardiovasc Qual Outcomes. 2014;7:640-647.',
    url: 'https://doi.org/10.1161/CIRCOUTCOMES.114.000967',
  },
};

const rule = (
  id: string,
  instrumentCode: string,
  instrumentVersion: string,
  purpose: ClinicalReferencePurpose,
  evidenceStatus: ClinicalEvidenceStatus,
  reportLabel: string,
  citationIds: string[],
  classificationAvailable = false,
): ClinicalReferenceRuleDefinition => ({
  id,
  version: 1,
  instrumentCode,
  instrumentVersion,
  purpose,
  evidenceStatus,
  lifecycle: 'active',
  reportLabel,
  citationIds,
  classificationAvailable,
});

export const CLINICAL_REFERENCE_RULES: readonly ClinicalReferenceRuleDefinition[] = [
  rule('ISWT_PROBST_BR_2012', 'ISWT', 'ISWT_PROBST_EXTENDED_2012', 'healthy_reference', 'validated', 'Probst et al. (2012) — referência brasileira do ISWT estendido', ['SINGH_1992_ISWT', 'PROBST_2012_ISWT']),
  rule('ESWT_REVILL_HILL', 'ESWT', 'ESWT_REVILL_1999', 'descriptive', 'validated', 'Revill et al. (1999); Hill et al. (2012) — protocolo ESWT', ['REVILL_1999_ESWT', 'HILL_2012_ESWT']),
  rule('MST6_ALBUQUERQUE_BR_2022', '6MST', '6MST_ALBUQUERQUE_2022', 'healthy_reference', 'validated', 'Albuquerque et al. (2022) — equação brasileira do 6MST', ['ALBUQUERQUE_2022_6MST']),
  rule('CHESTER_PROTOCOL', 'CHESTER', 'CHESTER_PROTOCOL_RECORDED', 'descriptive', 'validated', 'Sykes e Roberts (2004); Karloh et al. (2013) — versão conforme altura do degrau', ['SYKES_2004_CHESTER', 'KARLOH_2013_CHESTER']),
  rule('HANDGRIP_IGRIPS_2025', 'HANDGRIP', 'HANDGRIP_IGRIPS_2025', 'healthy_reference', 'validated', 'Tomkinson et al. (2025) — percentis internacionais iGRIPS', ['TOMKINSON_2025_HANDGRIP']),
  rule('PERIPHERAL_GARCIA_RESEARCH', 'PERIPHERAL_MUSCLE_STRENGTH', 'GARCIA_2024_RESEARCH_PREVIEW', 'healthy_reference', 'proposed', 'Garcia et al. (2024) — equações em modo de validação; limite inferior esperado indisponível', ['GARCIA_2024_PERIPHERAL']),
  rule('ONE_RM_MAYHEW_WATHAN', 'ONE_RM', 'ONE_RM_ESTIMATED_2026', 'descriptive', 'institutional', 'Mayhew (1992) e Wathan (1994) — estimativas exibidas separadamente; média institucional', ['MAYHEW_1992_1RM', 'WATHAN_1994_1RM']),
  rule('PIMAX_NEDER_1999', 'PIMAX', 'PIMAX_NEDER_1999', 'healthy_reference', 'derived', 'Neder et al. (1999) — previsto e limite inferior esperado derivado; sem bandas percentuais de gravidade', ['NEDER_1999_RESP_PRESSURES'], true),
  rule('PEMAX_NEDER_1999', 'PEMAX', 'PEMAX_NEDER_1999', 'healthy_reference', 'derived', 'Neder et al. (1999) — previsto e limite inferior esperado derivado; sem bandas percentuais de gravidade', ['NEDER_1999_RESP_PRESSURES'], true),
  rule('SINDEX_REIS_2025', 'S_INDEX', 'SINDEX_REIS_2025', 'healthy_reference', 'validated', 'Reis et al. (2025) — equação e limite inferior esperado por idade/sexo', ['REIS_2025_SINDEX'], true),
  rule('SINDEX_SOUZA_2025', 'S_INDEX', 'SINDEX_SOUZA_2025', 'healthy_reference', 'derived', 'Souza et al. (2025) — equação antropométrica; limite inferior esperado derivado', ['SOUZA_2025_SINDEX'], true),
  rule('RESP_ENDURANCE_ERS_2019', 'RESP_ENDURANCE', 'RESP_ENDURANCE_PROTOCOL_SPECIFIC', 'descriptive', 'validated', 'Laveneziana et al. (2019) — interpretação específica do protocolo', ['LAVENEZIANA_2019_ENDURANCE']),
  rule('PEF_JI_CHINA_2021', 'PEAK_FLOW', 'PEF_JI_CHINA_2021', 'healthy_reference', 'validated', 'Ji et al. (2021) — referência chinesa de 40 a 85 anos', ['JI_2021_PEF']),
  rule('PCF_BIANCHI_2008_PEDIATRIC', 'PEAK_COUGH_FLOW', 'PCF_HEALTHY_REFERENCE_V2', 'healthy_reference', 'validated', 'Bianchi e Baiardi (2008) — percentis P5, P50 e P95 por idade e sexo; tosse espontânea máxima, sentado e com máscara', ['BIANCHI_2008_COUGH'], true),
  rule('PCF_CARDOSO_2012_ADULT_MEAN', 'PEAK_COUGH_FLOW', 'PCF_HEALTHY_REFERENCE_V2', 'healthy_reference', 'proposed', 'Cardoso et al. (2012) — média de três manobras por idade e sexo (18 a 40 anos); comparação com a média da amostra, sem limite de normalidade publicado', ['CARDOSO_2012_COUGH']),
  { ...rule('PCF_ADULT_NMD_ATTENTION', 'PEAK_COUGH_FLOW', 'PCF_ERS_2019', 'screening', 'proposed', 'ERS (2019); Childs et al. (2024); Brennan et al. (2022) — faixas de atenção em adultos com doença neuromuscular; 160 L/min tem contexto de extubação/decanulação', ['LAVENEZIANA_2019_ENDURANCE', 'KHAN_2023_NEUROMUSCULAR', 'CHILDS_2024_DMD', 'BACH_1996_COUGH', 'BRENNAN_2022_COUGH'], true), version: 2 },
  rule('POSTURAL_VC_DESCRIPTIVE', 'CV_POSTURAL', 'POSTURAL_VC_2001', 'descriptive', 'derived', 'Fromageot et al. (2001) — comparação sentada/supina; achado descritivo, sem diagnóstico automático', ['FROMAGEOT_2001_POSTURAL_VC', 'LAVENEZIANA_2019_ENDURANCE']),
  rule('SNIP_ERS_DESCRIPTIVE', 'SNIP', 'SNIP_ERS_2019', 'descriptive', 'validated', 'ERS (2019) — medida complementar à PImax, interpretada no contexto clínico', ['LAVENEZIANA_2019_ENDURANCE', 'KHAN_2023_NEUROMUSCULAR']),
  rule('ACT_BR_ROXO_2010', 'ACT', 'ACT_BR_ROXO_2010', 'disease_status', 'validated', 'Roxo et al. (2010) — ACT brasileiro: ≤18 não controlada; ≥19 controlada', ['NATHAN_2004_ACT', 'ROXO_2010_ACT'], true),
  rule('MMRC_BR_DESCRIPTIVE', 'mMRC', 'MMRC_BR_KOVELIS_2008', 'descriptive', 'validated', 'Bestall et al. (1999); Kovelis et al. (2008) — escala mMRC de 0 a 4', ['BESTALL_1999_MMRC', 'KOVELIS_2008_MMRC_PFSDQ']),
  rule('PFSDQM_BR_2008', 'PFSDQ-M', 'PFSDQM_BR_KOVELIS_2008', 'descriptive', 'validated', 'Lareau et al. (1998); Kovelis et al. (2008) — domínios do PFSDQ-M', ['LAREAU_1998_PFSDQ', 'KOVELIS_2008_MMRC_PFSDQ']),
  rule('HADS_BR_BOTEGA_1995', 'HADS', 'HADS_BR_BOTEGA_1995', 'screening', 'validated', 'Botega et al. (1995) — HADS-A/HADS-D: rastreio positivo ≥9', ['ZIGMOND_1983_HADS', 'BOTEGA_1995_HADS'], true),
  rule('PSQI_BR_BERTOLAZI_2011', 'PSQI', 'PSQI_BR_BERTOLAZI_2011', 'screening', 'validated', 'Buysse et al. (1989); Bertolazi et al. (2011) — escore global >5', ['BUYSSE_1989_PSQI', 'BERTOLAZI_2011_PSQI'], true),
  rule('AQLQ_JUNIPER_DESCRIPTIVE', 'AQLQ', 'AQLQ_VERSION_RECORDED', 'descriptive', 'validated', 'Juniper et al. (1992) — médias de 1 a 7; sem faixas basais de gravidade', ['JUNIPER_1992_AQLQ']),
  rule('SGRQ_JONES_DESCRIPTIVE', 'SGRQ', 'SGRQ_VERSION_RECORDED', 'descriptive', 'validated', 'Jones et al. (1992) — escore 0–100; sem quartis de gravidade', ['JONES_1992_SGRQ']),
  rule('LCADL_BR_CARPES_2008', 'LCADL', 'LCADL_BR_15_CARPES_2008', 'descriptive', 'validated', 'Garrod et al. (2000); Carpes et al. (2008) — LCADL brasileira de 15 itens', ['GARROD_2000_LCADL', 'CARPES_2008_LCADL']),
  rule('MLHFQ_RECTOR_DESCRIPTIVE', 'MLHFQ', 'MLHFQ_21_RECORDED', 'descriptive', 'validated', 'Rector et al. (1993) — escore 0–105; bandas de Behlouli são propostas', ['RECTOR_1993_MLHFQ', 'BEHLOULI_2009_MLHFQ']),
  rule('SAQ_OFFICIAL_BLOCKED', 'SAQ', 'SAQ_19_OR_7_LICENSED', 'disease_status', 'unavailable', 'SAQ-19 ou SAQ-7 oficial — requer versão e algoritmo licenciados', ['SPERTUS_1995_SAQ', 'CHAN_2014_SAQ7']),
];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

const isRuleSnapshot = (value: unknown): value is ClinicalReferenceRuleSnapshot => {
  const record = asRecord(value);
  return typeof record.id === 'string'
    && Number.isSafeInteger(record.version)
    && typeof record.purpose === 'string'
    && typeof record.evidenceStatus === 'string'
    && typeof record.reportLabel === 'string'
    && Array.isArray(record.citationIds)
    && record.citationIds.every((item) => typeof item === 'string');
};

const isExcludedRuleSnapshot = (
  value: unknown,
): value is ClinicalReferenceExcludedRuleSnapshot => {
  const record = asRecord(value);
  return typeof record.ruleId === 'string'
    && Number.isSafeInteger(record.ruleVersion)
    && typeof record.reason === 'string'
    && typeof record.explanation === 'string';
};

const isCitation = (value: unknown): value is ClinicalCitation => {
  const record = asRecord(value);
  return typeof record.id === 'string'
    && typeof record.shortLabel === 'string'
    && typeof record.citation === 'string'
    && typeof record.url === 'string';
};

export const normalizeClinicalReferenceDecision = (
  value: unknown,
): ClinicalReferenceDecisionSnapshot | null => {
  const record = asRecord(value);
  if (
    typeof record.engineVersion !== 'string'
    || typeof record.evaluatedAt !== 'string'
    || typeof record.instrumentCode !== 'string'
    || typeof record.instrumentVersion !== 'string'
    || typeof record.descriptiveOnly !== 'boolean'
    || !Array.isArray(record.selectedRules)
    || !record.selectedRules.every(isRuleSnapshot)
    || !Array.isArray(record.excludedRules)
    || !record.excludedRules.every(isExcludedRuleSnapshot)
    || !Array.isArray(record.citations)
    || !record.citations.every(isCitation)
  ) {
    return null;
  }

  return {
    engineVersion: record.engineVersion,
    evaluatedAt: record.evaluatedAt,
    instrumentCode: record.instrumentCode,
    instrumentVersion: record.instrumentVersion,
    descriptiveOnly: record.descriptiveOnly,
    selectedRules: record.selectedRules,
    excludedRules: record.excludedRules,
    citations: record.citations,
  };
};

export const resolveClinicalInstrumentVersion = (
  testKey: string,
  testVersion: number,
  payload?: unknown,
): string => {
  const record = asRecord(payload);
  const explicit = typeof record.instrumentVersion === 'string'
    ? record.instrumentVersion.trim()
    : '';
  if (explicit) return explicit;

  if (testKey === 'LCADL') {
    return testVersion >= 2 ? 'LCADL_BR_15_CARPES_2008' : 'LCADL_ADAPTED_12_LEGACY';
  }
  if (testKey === 'SAQ') return 'ANGINA_ADAPTED_16_LEGACY';
  if (testKey === 'HADS') {
    return testVersion >= 2 ? 'HADS_BR_BOTEGA_1995' : 'HADS_SCORING_LEGACY_V1';
  }
  if (testKey === 'mMRC') return 'MMRC_BR_KOVELIS_2008';
  if (testKey === 'PEAK_COUGH_FLOW') return testVersion >= 2 ? 'PCF_HEALTHY_REFERENCE_V2' : 'PCF_ERS_2019';

  const matching = CLINICAL_REFERENCE_RULES.find((item) => item.instrumentCode === testKey);
  return matching?.instrumentVersion ?? `${testKey}_V${Math.max(1, testVersion)}`;
};

const toRuleSnapshot = (
  definition: ClinicalReferenceRuleDefinition,
): ClinicalReferenceRuleSnapshot => ({
  id: definition.id,
  version: definition.version,
  purpose: definition.purpose,
  evidenceStatus: definition.evidenceStatus,
  reportLabel: definition.reportLabel,
  citationIds: [...definition.citationIds],
});

export const buildClinicalReferenceDecision = (input: {
  testKey: string;
  testVersion: number;
  payload?: unknown;
  evaluatedAt?: string;
}): ClinicalReferenceDecisionSnapshot => {
  const instrumentVersion = resolveClinicalInstrumentVersion(
    input.testKey,
    input.testVersion,
    input.payload,
  );
  const definitions = CLINICAL_REFERENCE_RULES.filter(
    (item) => item.instrumentCode === input.testKey,
  );
  const selected: ClinicalReferenceRuleDefinition[] = [];
  const excluded: ClinicalReferenceExcludedRuleSnapshot[] = [];
  const coughPayload = input.testKey === 'PEAK_COUGH_FLOW' ? normalizePeakCoughFlowPayload(input.payload) : null;
  const coughSummary = input.testKey === 'PEAK_COUGH_FLOW' ? summarizePeakCoughFlowPayload(input.payload) : null;

  definitions.forEach((definition) => {
    if (input.testKey === 'PEAK_COUGH_FLOW') {
      const age = coughPayload?.ageYears;
      if (definition.purpose === 'healthy_reference') {
        const reference = coughSummary?.referenceAssessment;
        const expectedReference = definition.id === 'PCF_BIANCHI_2008_PEDIATRIC' ? 'BIANCHI_2008' : 'CARDOSO_2012';
        if (!reference?.available || reference.referenceId !== expectedReference) {
          const completedAge = age == null ? null : Math.floor(age);
          const ageOutside = completedAge != null && (expectedReference === 'BIANCHI_2008'
            ? completedAge < 4 || completedAge > 18 : completedAge < 18 || completedAge > 40);
          const protocolExplanation = expectedReference === 'BIANCHI_2008'
            ? 'Bianchi requer idade completa de 4 a 18 anos, sexo informado e tosse espontânea máxima, sentado e com máscara oronasal.'
            : 'Cardoso requer idade completa de 18 a 40 anos, sexo informado e a média de exatamente três manobras espontâneas aceitas, sentado e com bocal; a média da amostra não é um limite de normalidade.';
          excluded.push({ ruleId: definition.id, ruleVersion: definition.version,
            reason: age == null || !coughPayload?.sex || coughSummary?.bestUnassistedFlow == null
              ? 'missing_input' : ageOutside ? 'out_of_age_range' : 'wrong_protocol',
            explanation: reference?.referenceId === expectedReference && !ageOutside
              ? reference.reason || protocolExplanation : protocolExplanation,
          });
          return;
        }
      } else if (definition.id === 'PCF_ADULT_NMD_ATTENTION' && !coughSummary?.classifiable) {
        excluded.push({
          ruleId: definition.id,
          ruleVersion: definition.version,
          reason: age == null ? 'missing_input' : age < 18 ? 'out_of_age_range' : coughPayload?.clinicalContext?.neuromuscularDisease !== true ? 'wrong_diagnosis' : 'wrong_protocol',
          explanation: 'As faixas de atenção de 160 e 270 L/min exigem adulto com doença neuromuscular e medida espontânea por bocal ou máscara oronasal. Este gate é independente das referências saudáveis por idade e sexo.',
        });
        return;
      }
    }
    if (input.testKey === 'SAQ') {
      excluded.push({
        ruleId: definition.id,
        ruleVersion: definition.version,
        reason: 'unlicensed_version',
        explanation: 'O registro atual usa a versão adaptada de 16 respostas e não pode ser interpretado como SAQ-19 ou SAQ-7.',
      });
      return;
    }
    if (input.testKey === 'LCADL' && instrumentVersion === 'LCADL_ADAPTED_12_LEGACY') {
      excluded.push({
        ruleId: definition.id,
        ruleVersion: definition.version,
        reason: 'legacy_instrument_version',
        explanation: 'A versão legada tem 12 itens e não pode receber o escore ou os cortes da LCADL validada de 15 itens.',
      });
      return;
    }
    if (input.testKey === 'HADS' && instrumentVersion === 'HADS_SCORING_LEGACY_V1') {
      excluded.push({
        ruleId: definition.id,
        ruleVersion: definition.version,
        reason: 'legacy_instrument_version',
        explanation: 'A versão legada não registra a chave de pontuação validada item a item; o resultado é apresentado sem classificação clínica.',
      });
      return;
    }
    if (input.testKey === 'PERIPHERAL_MUSCLE_STRENGTH') {
      excluded.push({
        ruleId: definition.id,
        ruleVersion: definition.version,
        reason: 'classification_unavailable',
        explanation: 'Garcia et al. não publicam um limite inferior esperado reproduzível e a unidade das equações ainda requer confirmação; não classificar fraqueza.',
      });
      return;
    }
    if (input.testKey === 'S_INDEX') {
      const payload = asRecord(input.payload);
      const summary = asRecord(payload.summary);
      const selectedEquation = typeof payload.referenceEquation === 'string'
        ? payload.referenceEquation
        : typeof summary.referenceEquation === 'string'
          ? summary.referenceEquation
          : 'souza_2025_anthropometric';
      const definitionMatches =
        (definition.id === 'SINDEX_REIS_2025' && selectedEquation === 'reis_2025_hgdul')
        || (definition.id === 'SINDEX_SOUZA_2025' && selectedEquation === 'souza_2025_anthropometric');
      if (!definitionMatches) {
        excluded.push({
          ruleId: definition.id,
          ruleVersion: definition.version,
          reason: selectedEquation === 'souza_2022_height_age'
            ? 'legacy_instrument_version'
            : 'wrong_protocol',
          explanation: selectedEquation === 'souza_2022_height_age'
            ? 'A avaliação usa a equação preliminar legada de 2022; a regra Souza 2025 não é aplicada retroativamente.'
            : `A avaliação selecionou a equação ${selectedEquation}; esta regra pertence a outra equação de referência.`,
        });
        return;
      }
    }
    if (input.testKey === 'PEAK_FLOW') {
      const payload = asRecord(input.payload);
      const sourceKey = typeof payload.referenceSourceKey === 'string'
        ? payload.referenceSourceKey
        : 'ji_2021_charls';
      const referenceMode = typeof payload.referenceMode === 'string'
        ? payload.referenceMode
        : 'predicted_reference';
      if (
        referenceMode !== 'predicted_reference'
        || (sourceKey !== 'ji_2021_charls' && sourceKey !== 'zhao_2021_charls')
      ) {
        excluded.push({
          ruleId: definition.id,
          ruleVersion: definition.version,
          reason: 'wrong_protocol',
          explanation: 'A referência Ji et al. (2021) só é elegível quando o modo previsto CHARLS foi selecionado; zonas pelo melhor pessoal usam uma regra distinta.',
        });
        return;
      }
    }
    selected.push(definition);
  });

  const citationIds = new Set(definitions.flatMap((item) => item.citationIds));
  const citations = [...citationIds]
    .map((id) => CLINICAL_CITATIONS[id])
    .filter((citation): citation is ClinicalCitation => Boolean(citation));

  return {
    engineVersion: CLINICAL_REFERENCE_ENGINE_VERSION,
    evaluatedAt: input.evaluatedAt ?? new Date().toISOString(),
    instrumentCode: input.testKey,
    instrumentVersion,
    descriptiveOnly:
      selected.length === 0
      || selected.every((item) => {
        if (!item.classificationAvailable) return true;

        const payload = asRecord(input.payload);
        if (input.testKey === 'PIMAX') {
          return summarizePimaxPayload(payload as unknown as PimaxResultPayload).lowerLimit == null;
        }
        if (input.testKey === 'PEMAX') {
          return summarizePemaxPayload(payload as unknown as PemaxResultPayload).lowerLimit == null;
        }
        if (input.testKey === 'S_INDEX') {
          return summarizeSIndexPayload(payload as unknown as SIndexResultPayload).llnSIndex == null;
        }
        return false;
      }),
    selectedRules: selected.map(toRuleSnapshot),
    excludedRules: excluded,
    citations,
  };
};

export const getClinicalReferencePrimaryLabel = (
  decision?: ClinicalReferenceDecisionSnapshot | null,
): string | null => decision?.selectedRules[0]?.reportLabel ?? null;
